// Empire Apps Chassis · connectors/health/web-bluetooth
// Web Bluetooth + GATT for Mi Band 8/9, BoAt smartwatches, generic
// Heart Rate Service (0x180D) + Heart Rate Measurement (0x2A37) wearables.
//
// All scanning + characteristic notifications are gated behind a user
// gesture in the browser. We expose a simple subscribe/unsubscribe surface.

import {
  type ConnectorAdapter,
  type ConnectorMode,
  type ConnectorResult,
  type ConnectorStatus,
  err,
  nowIso,
  ok,
  shortHash,
} from './_types.ts';

export interface BleHeartTick {
  readonly bpm: number;
  readonly atIso: string;
  readonly rrIntervals: readonly number[];
}

export interface BleDevice {
  readonly id: string;
  readonly name: string;
  readonly hwBrand: 'mi-band' | 'boat' | 'fitbit-ble' | 'generic';
  readonly connectedAtIso: string;
}

export interface BlePayload {
  readonly device: BleDevice | null;
  readonly recentTicks: readonly BleHeartTick[];
}

export interface BleStatus extends ConnectorStatus {
  readonly id: 'web-bluetooth';
  readonly deviceName: string | null;
}

interface BluetoothLike {
  requestDevice(opts: { filters?: readonly { services?: readonly (string | number)[] }[]; optionalServices?: readonly (string | number)[]; acceptAllDevices?: boolean }): Promise<{ id?: string; name?: string; gatt?: { connect(): Promise<{ getPrimaryService(s: string | number): Promise<{ getCharacteristic(c: string | number): Promise<{ startNotifications(): Promise<unknown>; addEventListener(name: string, cb: (e: { target: { value: DataView } }) => void): void }> }> }> } }>;
}

function bluetoothBridge(): BluetoothLike | null {
  const g = navigator as unknown as { bluetooth?: BluetoothLike };
  return g.bluetooth ?? null;
}

export class WebBluetoothConnector implements ConnectorAdapter<BleStatus, BlePayload> {
  readonly id = 'web-bluetooth';
  readonly displayName = 'Web Bluetooth wearable';
  readonly displayNameHi = 'Web Bluetooth · BLE heart-rate';
  readonly dpdpCategory = 'PHI-low · heart-rate stream from BLE wearable';
  readonly mode: ConnectorMode = 'live';
  private device: BleDevice | null = null;
  private ticks: BleHeartTick[] = [];
  private lastSync: string | null = null;
  private principalHash: string | null = null;
  private listeners = new Set<(tick: BleHeartTick) => void>();

  constructor(private readonly principalId: string) {}

  async getStatus(): Promise<BleStatus> {
    if (!this.principalHash) this.principalHash = await shortHash(this.principalId);
    return {
      id: this.id,
      mode: this.mode,
      connected: this.device !== null,
      lastSyncAt: this.lastSync,
      principalIdHash: this.principalHash,
      notes: this.device ? `streaming from ${this.device.name}` : 'pair a BLE heart-rate wearable',
      deviceName: this.device?.name ?? null,
    };
  }

  async connect(): Promise<ConnectorResult<BleStatus>> {
    const ble = bluetoothBridge();
    if (!ble) return err({ code: 'bluetooth-unavailable', message: 'Web Bluetooth not available in this browser', retriable: false });
    try {
      const dev = await ble.requestDevice({
        filters: [{ services: ['heart_rate'] }],
        optionalServices: ['battery_service', 'device_information'],
      });
      const server = await dev.gatt?.connect();
      if (!server) return err({ code: 'bluetooth-unavailable', message: 'GATT connect failed', retriable: true });
      const svc = await server.getPrimaryService('heart_rate');
      const char = await svc.getCharacteristic('heart_rate_measurement');
      await char.startNotifications();
      char.addEventListener('characteristicvaluechanged', (e) => this.onValue(e.target.value));
      const brand = guessBrand(dev.name ?? '');
      this.device = {
        id: dev.id ?? 'unknown',
        name: dev.name ?? 'BLE wearable',
        hwBrand: brand,
        connectedAtIso: nowIso(),
      };
      this.lastSync = nowIso();
      return ok(await this.getStatus());
    } catch (e) {
      return err({ code: 'auth-cancelled', message: String(e instanceof Error ? e.message : e), retriable: true });
    }
  }

  subscribe(cb: (tick: BleHeartTick) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private onValue(dv: DataView): void {
    const flags = dv.getUint8(0);
    const u16 = (flags & 0x01) === 1;
    const bpm = u16 ? dv.getUint16(1, true) : dv.getUint8(1);
    const rr: number[] = [];
    if ((flags & 0x10) !== 0) {
      const offset = u16 ? 3 : 2;
      for (let i = offset; i + 1 < dv.byteLength; i += 2) {
        rr.push(dv.getUint16(i, true) / 1024);
      }
    }
    const tick: BleHeartTick = { bpm, atIso: nowIso(), rrIntervals: rr };
    this.ticks.push(tick);
    if (this.ticks.length > 600) this.ticks.shift();
    for (const cb of this.listeners) cb(tick);
  }

  async disconnect(): Promise<void> {
    this.device = null;
    this.ticks = [];
    this.lastSync = null;
    this.listeners.clear();
  }

  async importPayload(): Promise<ConnectorResult<BlePayload>> {
    return ok({ device: this.device, recentTicks: this.ticks });
  }

  mockPayload(): BlePayload {
    const ticks: BleHeartTick[] = [];
    for (let i = 0; i < 30; i++) {
      ticks.push({
        bpm: 68 + Math.round(Math.sin(i * 0.4) * 8),
        atIso: new Date(Date.now() - (29 - i) * 1000).toISOString(),
        rrIntervals: [0.88 + Math.random() * 0.08],
      });
    }
    return {
      device: {
        id: 'mock-device',
        name: 'Mi Band 8 (mock)',
        hwBrand: 'mi-band',
        connectedAtIso: nowIso(),
      },
      recentTicks: ticks,
    };
  }
}

function guessBrand(name: string): BleDevice['hwBrand'] {
  const lc = name.toLowerCase();
  if (lc.includes('mi band') || lc.includes('xiaomi') || lc.includes('smart band')) return 'mi-band';
  if (lc.includes('boat') || lc.includes('crest') || lc.includes('wave')) return 'boat';
  if (lc.includes('fitbit')) return 'fitbit-ble';
  return 'generic';
}
